import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/apiClient';
import type {
  CreateProductStackDto,
  ProductStackDto,
  ProductStackOperationAcceptedDto,
  ProductStackOperationDto,
} from '../types/productStack';

const TERMINAL_STATES = new Set(['Running', 'Deleted', 'Failed']);

export const useProductStacks = () => {
  return useQuery<ProductStackDto[]>({
    queryKey: ['productStacks'],
    queryFn: () => apiClient.getProductStacks(),
    refetchInterval: (query) => {
      const stacks = query.state.data ?? [];
      const hasInFlight = stacks.some((stack) => !TERMINAL_STATES.has(stack.lifecycleState));
      return hasInFlight ? 10000 : false;
    },
    staleTime: 2000,
  });
};

export const useProductStackOperations = (productStackId: string | undefined) => {
  return useQuery<ProductStackOperationDto[]>({
    queryKey: ['productStackOperations', productStackId],
    queryFn: () => apiClient.getProductStackOperations(productStackId!),
    enabled: !!productStackId,
    refetchInterval: 10000,
    staleTime: 1000,
  });
};

export const useCreateProductStack = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ProductStackOperationAcceptedDto,
    Error,
    CreateProductStackDto
  >({
    mutationFn: (data) => apiClient.createProductStack(data),
    onSuccess: (accepted, submittedData) => {
      queryClient.setQueryData<ProductStackDto[]>(['productStacks'], (existing = []) => {
        const next: ProductStackDto = {
          id: accepted.productStackId,
          name: submittedData.name,
          environmentName: submittedData.environmentName,
          regionKey: submittedData.regionKey,
          createdByDisplayName: submittedData.createdByDisplayName,
          enableCdm: submittedData.enableCdm,
          lifecycleState: accepted.lifecycleState,
          statusReason: accepted.statusReason,
          irisUrl: undefined,
          bifrostUrl: undefined,
          createdOn: new Date().toISOString(),
          lastModifiedOn: new Date().toISOString(),
        };

        if (existing.some((stack) => stack.id === next.id)) {
          return existing;
        }

        return [next, ...existing];
      });

      queryClient.invalidateQueries({ queryKey: ['productStacks'] });
      queryClient.invalidateQueries({ queryKey: ['productStackOperations', accepted.productStackId] });
    },
  });
};

export const useDeleteProductStack = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ProductStackOperationAcceptedDto,
    Error,
    string
  >({
    mutationFn: (productStackId) => apiClient.requestDeleteProductStack(productStackId),
    onSuccess: (accepted) => {
      queryClient.invalidateQueries({ queryKey: ['productStacks'] });
      queryClient.invalidateQueries({ queryKey: ['productStackOperations', accepted.productStackId] });
    },
  });
};
